// Inline style factory for the Shift Control modal — same visual language as
// the other Attendance dialogs (DefaultLocationModal): solid surface, header
// with an accent icon tile, #2e7cba accent, 10px controls, 20px dialog radius.
import { dropdownPalette } from "../../../../components/ReusableDropdown";

export const ACCENT = "#2e7cba";
const ACCENT_HOVER = "#2563a8";

// `isMobile` (≤768px): dialogs become bottom sheets with tighter padding,
// segmented tabs stretch full width, and the schedule grid narrows its columns.
export const makeStyles = (theme, isRTL, isMobile = false) => {
  const dark = theme === "dark";
  const surface = dark ? "#1e293b" : "#ffffff";
  const surfaceAlt = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)";
  const borderColor = dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
  const border = `1px solid ${borderColor}`;
  const rowBorder = dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #f1f5f9";
  const textPrimary = dark ? "#f1f5f9" : "#0f172a";
  const muted = dark ? "#94a3b8" : "#64748b";
  const headBg = dark ? "#243247" : "#f8fafc";
  const start = isRTL ? "right" : "left";
  const dd = dropdownPalette(theme); // shared ReusableDropdown palette

  return {
    dark,
    isMobile,
    muted,
    accent: ACCENT,
    accentHover: ACCENT_HOVER,
    textPrimary,

    // ── dialog shell ──
    overlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
      zIndex: 9999, padding: isMobile ? 0 : "12px 24px", boxSizing: "border-box",
    },
    dialog: {
      background: surface,
      color: textPrimary,
      direction: isRTL ? "rtl" : "ltr",
      width: "min(1360px, 100%)",
      height: isMobile ? "94dvh" : "100%",
      maxHeight: isMobile ? "94dvh" : "calc(100vh - 24px)",
      borderRadius: isMobile ? "18px 18px 0 0" : 20,
      border,
      boxShadow: dark ? "0 24px 64px rgba(0,0,0,0.7)" : "0 24px 64px rgba(0,0,0,0.18)",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
      boxSizing: "border-box",
      minWidth: 0,
    },
    header: {
      padding: isMobile ? "12px 16px" : "14px 24px",
      borderBottom: border,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      flexWrap: "wrap", gap: isMobile ? 10 : 16, flexShrink: 0,
    },
    headerIcon: {
      width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 12, flexShrink: 0,
      background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT})`,
      boxShadow: `0 4px 14px ${ACCENT}55`,
      display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
    },
    title: { fontSize: isMobile ? 17 : 20, fontWeight: 700, margin: 0, color: textPrimary },
    subtitle: { fontSize: isMobile ? 12 : 13, lineHeight: 1.5, color: muted, margin: "4px 0 0 0" },
    body: {
      flex: 1, minHeight: 0,
      display: "flex", flexDirection: "column",
      padding: isMobile ? "12px 16px" : "16px 24px",
      overflowY: "auto", overflowX: "hidden",
    },
    closeBtn: {
      background: "transparent", border: "none", cursor: "pointer",
      color: muted, padding: 6, borderRadius: 8,
      display: "flex", alignItems: "center", flexShrink: 0,
      transition: "all 0.2s ease",
    },

    // ── segmented tabs ──
    tabs: {
      display: isMobile ? "flex" : "inline-flex", gap: 4, padding: 4, borderRadius: 12,
      background: surfaceAlt, border,
      ...(isMobile && { width: "100%", boxSizing: "border-box" }),
    },
    tab: (active) => ({
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      ...(isMobile && { flex: "1 1 0", minWidth: 0, minHeight: 40 }),
      padding: isMobile ? "8px 10px" : "8px 16px", borderRadius: 9, border: "none", cursor: "pointer",
      fontSize: 13, fontWeight: 600,
      background: active ? `linear-gradient(135deg, ${ACCENT}, ${ACCENT})` : "transparent",
      color: active ? "#fff" : muted,
      boxShadow: active ? `0 2px 8px ${ACCENT}44` : "none",
      transition: "all 0.2s ease",
    }),

    // ── tab bodies ──
    panel: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 },
    card: {
      background: surfaceAlt, border, borderRadius: 14, padding: isMobile ? 14 : 20, minWidth: 0,
    },
    toolbar: {
      display: "flex", flexWrap: "wrap", gap: isMobile ? 8 : 10, alignItems: "center",
      padding: isMobile ? "10px" : "10px 14px", borderRadius: 14, marginBottom: 12,
      background: surfaceAlt, border,
    },
    controls: { display: "flex", flexWrap: "wrap", gap: isMobile ? 8 : 10, alignItems: "center", marginBottom: 12 },
    // Matches ReusableDropdown's input so text fields and dropdowns line up.
    input: {
      minHeight: 44, padding: "10px 14px", borderRadius: 10,
      fontSize: 14, fontWeight: 500, fontFamily: "inherit", outline: "none",
      background: dd.inputBg, border: `1px solid ${dd.border}`, color: dd.text,
      colorScheme: dark ? "dark" : "light",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      minWidth: 0, maxWidth: "100%", boxSizing: "border-box",
    },
    // Labelled field inside the bulk panel (label above the control).
    field: (flex, minWidth) => ({ display: "flex", flexDirection: "column", gap: 6, flex, minWidth }),
    fieldLabel: { fontSize: 12, fontWeight: 600, color: muted, paddingInlineStart: 2 },
    countChip: (active) => ({
      display: "inline-flex", alignItems: "center", gap: 8, height: 44, padding: "0 14px",
      borderRadius: 10, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", boxSizing: "border-box",
      background: active ? (dark ? "rgba(46,124,186,0.18)" : "#eef5fb") : "transparent",
      color: active ? (dark ? "#7cb8e6" : ACCENT) : muted,
      border: active ? `1px solid ${ACCENT}55` : border,
      transition: "all 0.2s ease",
    }),
    checkRow: {
      height: 44, display: "inline-flex", alignItems: "center", gap: 10,
      fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    },
    // Shift legend chips above the grid.
    legendRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "0 0 10px" },
    legendChip: (color) => ({
      display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px 5px 9px",
      borderRadius: 999, fontSize: 12.5, lineHeight: 1.4, maxWidth: 260, cursor: "default",
      background: `${color}${dark ? "1f" : "14"}`, border: `1px solid ${color}55`,
    }),
    legendMoreBtn: {
      display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px",
      borderRadius: 999, fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, cursor: "pointer",
      background: "transparent", color: dark ? "#7cb8e6" : ACCENT,
      border: `1px dashed ${ACCENT}88`, fontFamily: "inherit",
    },
    legendName: { fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
    legendMeta: { display: "inline-flex", alignItems: "center", gap: 4, color: muted, fontWeight: 500 },
    legendSep: { width: 1, height: 12, background: borderColor, flexShrink: 0 },
    btn: (variant = "primary", disabled = false) => ({
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      minHeight: 44, padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled && variant !== "primary" ? 0.5 : 1,
      transition: "all 0.2s ease", border: "1px solid transparent", whiteSpace: "nowrap",
      boxSizing: "border-box",
      ...(variant === "primary" && (disabled
        ? { background: dark ? "rgba(148,163,184,0.16)" : "#e2e8f0", color: dark ? "#64748b" : "#94a3b8", fontWeight: 700 }
        : {
          background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT})`, color: "#fff", fontWeight: 700,
          boxShadow: `0 4px 14px ${ACCENT}44`,
        })),
      ...(variant === "ghost" && {
        background: "transparent", border, color: dark ? "#cbd5e1" : "#475569",
      }),
      ...(variant === "danger" && {
        background: dark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
        border: "1px solid rgba(239,68,68,0.35)", color: "#ef4444",
      }),
    }),
    iconBtn: (color) => ({
      background: "transparent", border: "none", cursor: "pointer",
      padding: 6, borderRadius: 8, display: "inline-flex",
      color: color || muted,
    }),
    checkbox: { margin: 0, flexShrink: 0 }, // look comes from the .sc-check class
    statusChip: (active) => ({
      display: "inline-block", padding: "4px 10px", borderRadius: 999,
      fontSize: 12, fontWeight: 700,
      background: active
        ? (dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)")
        : (dark ? "rgba(148,163,184,0.15)" : "rgba(100,116,139,0.1)"),
      color: active ? "#22c55e" : muted,
    }),
    badge: (color) => ({
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
      background: `${color}22`, color,
    }),
    dot: (color) => ({
      width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0,
      display: "inline-block",
    }),

    // ── nested form dialog (new / edit shift) ──
    modal: {
      background: surface,
      borderRadius: isMobile ? "18px 18px 0 0" : 20, padding: isMobile ? "16px 16px 20px" : "24px 28px", width: "100%", maxWidth: 560,
      maxHeight: isMobile ? "92dvh" : "88vh", overflowY: "auto", boxSizing: "border-box",
      border,
      boxShadow: dark ? "0 24px 64px rgba(0,0,0,0.7)" : "0 24px 64px rgba(0,0,0,0.18)",
      direction: isRTL ? "rtl" : "ltr",
      color: textPrimary,
    },
    modalTitle: {
      fontSize: 18, fontWeight: 700, margin: "0 0 8px 0", paddingBottom: 14,
      borderBottom: border,
      display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, margin: "14px 0 6px", color: dark ? "#cbd5e1" : "#475569" },
    hint: { fontSize: 12.5, color: muted, lineHeight: 1.6 },
    grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))", gap: "0 12px" },
    emptyState: { textAlign: "center", padding: "48px 20px", color: muted },

    // ── schedule grid ──
    gridScroll: {
      flex: "1 1 auto", minHeight: 240,
      // Phones: own scroll box so the day header and name column stay pinned.
      ...(isMobile && { flex: "0 0 auto", maxHeight: "62dvh" }),
      overflow: "auto", WebkitOverflowScrolling: "touch",
      borderRadius: 14, border,
      background: surface,
    },
    gridTable: { borderCollapse: "separate", borderSpacing: 0, fontSize: 13, width: "100%" },
    gridHeadCell: (isToday, isFirst) => ({
      position: "sticky", top: 0, zIndex: isFirst ? 4 : 2,
      ...(isFirst && { [start]: 0 }),
      background: isToday ? (dark ? "#1d4f7c" : "#dbeafe") : headBg,
      padding: "10px 6px", fontWeight: 700, fontSize: 12,
      color: isToday ? (dark ? "#fff" : ACCENT) : muted,
      borderBottom: isToday ? `2px solid ${ACCENT}` : border,
      textAlign: "center", whiteSpace: "nowrap",
    }),
    gridNameCell: {
      position: "sticky", [start]: 0, zIndex: 1,
      background: surface,
      padding: isMobile ? "6px 8px" : "7px 12px", minWidth: isMobile ? 120 : 190, maxWidth: isMobile ? 140 : 240,
      borderBottom: rowBorder,
      [isRTL ? "borderLeft" : "borderRight"]: border,
    },
    gridCell: {
      padding: "5px 4px", minWidth: isMobile ? 82 : 96,
      borderBottom: rowBorder,
      textAlign: "center",
    },
    todayTint: dark ? "rgba(46,124,186,0.10)" : "rgba(219,234,254,0.45)",
    cellSelect: ({ bg, fg, dirty, weekend }) => ({
      width: "100%", minWidth: isMobile ? 76 : 88, padding: isMobile ? "9px 4px" : "7px 4px", borderRadius: 8,
      fontSize: 12, fontWeight: 700, cursor: "pointer", outline: "none",
      appearance: "none", WebkitAppearance: "none", textAlign: "center", textAlignLast: "center",
      colorScheme: dark ? "dark" : "light",
      background: bg || (weekend
        ? (dark ? "repeating-linear-gradient(45deg, rgba(148,163,184,0.10) 0 6px, transparent 6px 12px)" : "repeating-linear-gradient(45deg, #f1f5f9 0 6px, #fff 6px 12px)")
        : (dark ? "rgba(255,255,255,0.04)" : "#f8fafc")),
      color: fg || muted,
      border: dirty ? "2px solid #f59e0b" : (dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0"),
      transition: "border-color 0.15s ease",
    }),
    // Dialog footer (outside the scrolling body, so it is never clipped).
    footer: {
      flexShrink: 0,
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
      justifyContent: "space-between",
      padding: isMobile ? "10px 16px" : "10px 24px",
      borderTop: border,
      background: dark ? "rgba(15,23,42,0.35)" : "#f8fafc",
    },
    unsavedNote: {
      display: "inline-flex", alignItems: "center", gap: 10,
      fontSize: 14, fontWeight: 700, color: textPrimary,
    },
    unsavedDot: {
      width: 10, height: 10, borderRadius: "50%", background: "#f59e0b", flexShrink: 0,
      boxShadow: "0 0 0 4px rgba(245,158,11,0.18)",
    },
  };
};

// Scoped scrollbar rules. Vertical scrollbars are hidden (content still scrolls
// with wheel / touch); the schedule grid keeps a slim horizontal bar for the
// wide month view. Class selectors outrank the page-wide `div::-webkit-scrollbar`
// rules that DefaultLocationModal injects.
export const scrollbarCss = (dark) => `
  .sc-noscroll { scrollbar-width: none; -ms-overflow-style: none; }
  .sc-noscroll::-webkit-scrollbar { width: 0; height: 0; display: none; }
  .sc-grid::-webkit-scrollbar { width: 0; height: 6px; }
  .sc-grid::-webkit-scrollbar-track { background: transparent; }
  .sc-grid::-webkit-scrollbar-thumb {
    background: ${dark ? "rgba(148,163,184,0.45)" : "rgba(100,116,139,0.4)"};
    border-radius: 10px; border: none;
  }
  .sc-grid::-webkit-scrollbar-corner { background: transparent; }
  @supports not selector(::-webkit-scrollbar) {
    .sc-grid { scrollbar-width: thin; scrollbar-color: ${dark ? "rgba(148,163,184,0.45)" : "rgba(100,116,139,0.4)"} transparent; }
  }
  .sc-check {
    appearance: none; -webkit-appearance: none; margin: 0;
    width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0; cursor: pointer;
    border: 1.5px solid ${dark ? "rgba(148,163,184,0.5)" : "#94a3b8"};
    background: transparent; transition: all 0.15s ease; vertical-align: middle;
  }
  .sc-check:hover { border-color: ${ACCENT}; }
  .sc-check:checked {
    border-color: ${ACCENT};
    background: ${ACCENT} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E") center / 11px no-repeat;
  }
  .sc-check:focus-visible { outline: none; box-shadow: 0 0 0 3px ${dark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.18)"}; }
  .sc-input:hover { border-color: ${dark ? "rgba(148,163,184,0.5)" : "#94a3b8"} !important; }
  .sc-input:focus { border-color: ${ACCENT} !important; box-shadow: 0 0 0 3px ${dark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.18)"}; }
  .sc-input::placeholder { color: ${dark ? "#64748b" : "#94a3b8"}; opacity: 1; }
  @keyframes rdd-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
  .sc-footer:empty { display: none !important; }
  .sc-close:hover { background: ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}; }
  .sc-cell:hover { border-color: ${ACCENT} !important; }
  .sc-ghost:not(:disabled):hover { background: ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"} !important; }
  .sc-primary:not(:disabled):hover { background: linear-gradient(135deg, ${ACCENT_HOVER}, #1e40af) !important; }
`;
